import { useMutation } from "@apollo/client";

import {
  BULK_DELETE_OEM_CUSTOMERS_MUTATION,
  CREATE_OEM_CUSTOMER_V2_MUTATION,
  DELETE_OEM_CUSTOMER_MUTATION,
  LIST_OWN_OEM_ALL_TICKETS_QUERY,
  Customer,
  UPDATE_OEM_CUSTOMER_MUTATION,
  ASSIGN_COMPANIES_TO_PARENT_MUTATION,
  ASSIGN_PARENTS_TO_COMPANY_MUTATION,
  UNASSIGN_COMPANY_FROM_PARENT_MUTATION,
} from "~/api";
import { errorsHandler } from "~/transformers";
import { evictHierarchyCaches } from "~/utils/graphql";

export const deleteFacility = () => {
  const [deleteCustomer, { loading, error }] = useMutation(
    DELETE_OEM_CUSTOMER_MUTATION,
  );

  return {
    loading,
    error,
    removeFacility: async (customerId) =>
      deleteCustomer({
        variables: { customerId },
        skip: !customerId,
        update(cache) {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listAllOwnOemCustomers",
          });
          cache.gc();

          // updating cache: Ticket List
          try {
            const ticketListCache = cache.readQuery({
              query: LIST_OWN_OEM_ALL_TICKETS_QUERY,
              variables: {},
            });
            if (!ticketListCache) return;

            const cloned = [...ticketListCache.listOwnOemAllTickets];
            const newTicketList = cloned.map((ticket) => {
              const t = { ...ticket };
              if (t.facility && t.facility._id === customerId) {
                t.facility = null;
              }
              return t;
            });

            cache.writeQuery({
              query: LIST_OWN_OEM_ALL_TICKETS_QUERY,
              variables: {},
              data: { listOwnOemAllTickets: newTicketList },
            });
          } catch (error) {
            console.error("[Facility Delete][Ticket List]", error);
          }
        },
      }),
  };
};

export const useCreateOemCustomer = () => {
  const [create, { loading, error }] = useMutation(
    CREATE_OEM_CUSTOMER_V2_MUTATION,
  );

  return {
    loading,
    error,
    createOemCustomer: async (input) =>
      create({
        variables: { input },
        update: (cache, { data: { createOemCustomerV2: ref } }) => {
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listAllOwnOemCustomers",
          });
          cache.gc();
        },
      }),
  };
};

export const useAssignCompaniesToParent = () => {
  const [assign, { loading, error }] = useMutation(
    ASSIGN_COMPANIES_TO_PARENT_MUTATION,
  );

  return {
    loading,
    error,
    mutation: ({ companyIds, parentId }) =>
      new Promise((resolve, reject) =>
        assign({
          variables: { input: { companyIds, parentId } },
          update(cache) {
            evictHierarchyCaches(cache, [parentId, ...(companyIds || [])]);
          },
        })
          .then(resolve)
          .catch((err) => reject(errorsHandler(err))),
      ),
  };
};

export const useAssignParentsToCompany = () => {
  const [assign, { loading, error }] = useMutation(
    ASSIGN_PARENTS_TO_COMPANY_MUTATION,
  );

  return {
    loading,
    error,
    mutation: ({ companyId, parentIds }) =>
      new Promise((resolve, reject) =>
        assign({
          variables: { input: { companyId, parentIds } },
          update(cache) {
            evictHierarchyCaches(cache, [companyId, ...(parentIds || [])]);
          },
        })
          .then(resolve)
          .catch((err) => reject(errorsHandler(err))),
      ),
  };
};

export const useUnassignCompanyFromParent = () => {
  const [unassign, { loading, error }] = useMutation(
    UNASSIGN_COMPANY_FROM_PARENT_MUTATION,
  );

  return {
    loading,
    error,
    mutation: ({ companyId, parentId }) =>
      new Promise((resolve, reject) =>
        unassign({
          variables: { input: { companyId, parentId } },
          update(cache) {
            evictHierarchyCaches(cache, [companyId, parentId]);
          },
        })
          .then(resolve)
          .catch((err) => reject(errorsHandler(err))),
      ),
  };
};

export const useUpdateOemCustomer = () => {
  const [updateCustomer, { loading, error }] = useMutation(
    UPDATE_OEM_CUSTOMER_MUTATION,
  );

  return {
    loading,
    error,
    updateOemCustomer: async (input, { skipCacheUpdate = false } = {}) =>
      updateCustomer({
        variables: { input },
        update(cache, { data: { updateOemCustomer: ref } }) {
          if (skipCacheUpdate) {
            return;
          }
          cache.modify({
            id: cache.identify({ __typename: "Customer", ref: ref._id }),
            fields: {
              getOwnOemCustomerById() {
                const newRef = cache.writeFragment({
                  data: ref,
                  fragment: Customer.fragments.CustomerFullData,
                  fragmentName: "CustomerFullData",
                });

                return newRef;
              },
            },
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listOwnOemAllTickets",
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listAllOwnOemMachines",
          });
          cache.evict({
            id: "ROOT_QUERY",
            fieldName: "listAllOwnOemAssets",
          });
          // Evict after delay to allow the MongoDB trigger to create the activity log
          setTimeout(() => {
            cache.evict({
              id: "ROOT_QUERY",
              fieldName: "listOwnOemActivityLogs",
            });
            cache.gc();
          }, 2000);
        },
      }),
  };
};

export const useBulkDeleteCustomers = () => {
  const [bulkDelete] = useMutation(BULK_DELETE_OEM_CUSTOMERS_MUTATION);

  const bulkDeleteCustomers = async (
    customerIds,
    deleteSubCompanies = false,
  ) => {
    const { data } = await bulkDelete({
      variables: { customerIds, deleteSubCompanies },
      update(cache) {
        ["listAllOwnOemCustomers", "listOwnOemKanbanCustomers"].forEach(
          (fieldName) => cache.evict({ id: "ROOT_QUERY", fieldName }),
        );
        cache.gc();
      },
    });
    return data.bulkDeleteOemCustomers;
  };

  return { bulkDeleteCustomers };
};
